import * as React from 'react'
import { Carousel } from '@amzn/dvui/dist/Carousel'
import Header from './Header/Header'
import Card from './Card/Card'
import * as style from './FacetedCarousel.module.scss'
import * as cardStyle from './Card/Card.module.scss'
import * as csm from '../../../utils/csm'
import InView from 'react-intersection-observer'
import * as cn from 'classnames'
import { CollectionProps } from '../Collection';
import { StorefrontStateItem } from '../../../containers/Storefront';
import { safeBool, WPSClient, ScheduleEntry, ItemProgressBar } from 'DVWebNodeUtils'


interface FacetedCarouselState {
  items: StorefrontStateItem[]
  isEditActive: boolean
  isDirty: boolean
  truncateItemList: boolean
  clock?: number
}

const CAROUSEL_THRESHOLD = 8
const DOUBLE_DECKER_CAROUSEL_THRESHOLD = 14
const PRELOADING_LOADING_CARDS_MULTIPLE = 2
const PAGINATE_COLLECTION_PAGE_SIZE = 40
const WPS = new WPSClient()
const CHANNEL_REFRESH_TIME = 5 * 1000


const overwriteWithCurrentSchedule = (clock: number | undefined, card: StorefrontStateItem) => {
  if (!clock)
    return card
  const currentItem = currentBroadcast(clock, card)
  if (!currentItem)
    return card

  return {
    ...card,
    ...currentItem.metadata,
    progress: toProgressBar(clock, currentItem)
  }
}

const toProgressBar = (clock: number, schedule: ScheduleEntry): ItemProgressBar => {
  const duration = schedule.end - schedule.start
  const elapsed = clock - schedule.start
  const percentage = elapsed / duration * 100
  return { percentage: percentage, isLive: true }
}

const currentBroadcast = (clock: number, card: StorefrontStateItem) => {
  const currentTime = clock
  const currentZenoItems = card.station && card.station.schedule.filter(e => e.start < currentTime && e.end > currentTime)
  const maybeCurrentZenoItem = currentZenoItems && currentZenoItems[0]
  return maybeCurrentZenoItem
}


class FacetedCarousel extends React.PureComponent<CollectionProps, FacetedCarouselState> {

  state: FacetedCarouselState
  containerRef: React.RefObject<HTMLDivElement>
  isHoriPagActive: boolean
  isHoriPageInitPreloadActive?: boolean
  isHoriPageWithPreloadAfterClickActive?: boolean
  hasMoreItems: boolean
  intersectionObserver?: IntersectionObserver
  clockUpdate?: number

  isGettingEnriched = false
  isPartiallyEnriched = false
  isFullyEnriched = false
  isFetchingMoreItems = false
  hasLoadingItems = false
  hasObservedItemTargetsSet = false
  isFirstHoriPagCall = true

  constructor(props: CollectionProps) {
    super(props)
    this.isHoriPagActive = props.featureSwitches &&
      (props.featureSwitches.horiPageInitPreload ||
        props.featureSwitches.horiPageWithoutInitPreload ||
        props.featureSwitches.horiPageWithPreloadAfterClick) || false

    const clock = this.props.items.some(item => Boolean(item.station)) ? new Date().getTime() : undefined

    this.state = {
      items: props && props.items,
      isEditActive: false,
      isDirty: false,
      truncateItemList: this.isHoriPagActive ? false : Boolean(props.featureSwitches && props.featureSwitches.delayItemsRendering),
      clock: clock
    }

    this.containerRef = React.createRef()

    this.isHoriPageInitPreloadActive = props.featureSwitches && props.featureSwitches.horiPageInitPreload
    this.isHoriPageWithPreloadAfterClickActive = props.featureSwitches && props.featureSwitches.horiPageWithPreloadAfterClick
    this.hasMoreItems = props.paginationTargetId !== undefined
  }

  componentDidCatch = (error: Error) => {
    csm.count('DVWeb::Storefront::JSFatal::FacetedCarousel')
    csm.logError(error, '[DVWeb::Storefront] Error while trying to render a FacetedCarousel')
  }

  toggleEdit = () => {
    this.setState({ isEditActive: !this.state.isEditActive })
  }

  setCollectionDirty() {
    if (!this.state.isDirty) {
      this.setState({ isDirty: true, truncateItemList: false })
    }
  }

  carouselScrollInteractionHandler = () => {
    csm.count('CarouselScroll')
    this.carouselInteractionHandler()
  }

  carouselNavClickInteractionHandler = () => {
    csm.count('CarouselPagination')
    this.carouselInteractionHandler()
  }

  maxNumberOfCardsShownInViewPort() {
    return this.props.collectionType === "DoubleDeckerCarousel" ? DOUBLE_DECKER_CAROUSEL_THRESHOLD : CAROUSEL_THRESHOLD
  }

  componentDidUpdate() {
    // If the carousel contains the focused element, but
    // another has more recently been interacted with, clear
    // the focus, to close the hover (if it's open)
    if (!this.props.collectionHasFocusForHover) {
      const activeElement = document.activeElement as HTMLElement
      const currentContainer = this.containerRef && this.containerRef.current
      if (currentContainer && activeElement && currentContainer.contains(activeElement))
        activeElement.blur()
    }
    if (this.props.filterOn)
      this.setCollectionDirty()

    if (this.isHoriPagActive && this.hasMoreItems) {
      if (!this.hasLoadingItems) this.addLoadingItems()
      else this.ensureTargetsAreObserved()
    }
  }

  addLoadingItems() {
    this.hasLoadingItems = true
    const newItems = [...this.props.items, ...this.createLoadingItems(this.maxNumberOfCardsShownInViewPort())]

    this.props.updateCollection(this.props.collectionKey, newItems)
  }

  ensureTargetsAreObserved() {
    const intersectionObserver = this.intersectionObserver
    if (!intersectionObserver || !this.containerRef.current)
      return
    if (!this.hasObservedItemTargetsSet && this.hasLoadingItems) {
      const items = this.props.items
      const carouselItemContainers = this.containerRef.current.querySelectorAll('li > *')
      const maxNumberOfCardsShownInViewPort = this.maxNumberOfCardsShownInViewPort()

      for (let i = carouselItemContainers.length - 1; i >= 0 && !this.hasObservedItemTargetsSet; i--) {
        if (items[i].itemType === "Loading") {
          intersectionObserver.observe(carouselItemContainers[i])
        } else if (!this.isFirstHoriPagCall && (i === 0 || i === (carouselItemContainers.length - 1 -
          maxNumberOfCardsShownInViewPort * PRELOADING_LOADING_CARDS_MULTIPLE))) {
          intersectionObserver.observe(carouselItemContainers[i])
          this.hasObservedItemTargetsSet = true
        } else if (this.isFirstHoriPagCall && this.isHoriPageWithPreloadAfterClickActive && (i === 0 ||
          i === maxNumberOfCardsShownInViewPort + 1)) {
          intersectionObserver.observe(carouselItemContainers[i])
          this.hasObservedItemTargetsSet = true
        } else if (this.isFirstHoriPagCall && !this.isHoriPageWithPreloadAfterClickActive) {
          this.hasObservedItemTargetsSet = true
        }
      }
    }
  }

  fetchCompleted(newItems: StorefrontStateItem[]) {
    this.intersectionObserver.disconnect()
    this.hasObservedItemTargetsSet = false
    if (this.isFirstHoriPagCall) this.isFirstHoriPagCall = false
    this.hasLoadingItems = false
    this.isFetchingMoreItems = false
    const mainItems = this.props.items.filter(item => item.itemType !== "Loading")
    if (newItems.length === 0) this.hasMoreItems = false

    this.props.updateCollection(this.props.collectionKey, [...mainItems, ...newItems])
  }

  fetchMoreItems() {
    this.isFetchingMoreItems = true
    const props = this.props

    WPS.PaginateCollection({
      context: props.context,
      paginationTargetId: props.paginationTargetId,
      pageType: props.pageType ? props.pageType : "home",
      pageId: props.pageId ? props.pageId : "home",
      serviceToken: props.paginationServiceToken,
      startIndex: this.hasLoadingItems ? props.items.length - this.maxNumberOfCardsShownInViewPort() : props.items.length,
      pageSize: PAGINATE_COLLECTION_PAGE_SIZE,
      isEditable: props.edit !== undefined,
      collectionType: props.collectionType,
      facetType: props.facetType,
      tags: props.tags
    }).then(response => {
      this.fetchCompleted(response.items)
    }).catch(error => {
      this.props.logger.error(error)
      this.fetchCompleted([])
    })
  }

  componentDidMount() {
    if (this.state.clock)
      this.clockUpdate = window.setInterval(() => this.setState({ clock: new Date().getTime() }), CHANNEL_REFRESH_TIME)

    if (this.isHoriPagActive && this.hasMoreItems) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (entries.some(entry => entry.isIntersecting)) {
          if (!this.isFetchingMoreItems) this.fetchMoreItems()
        }
      }, { threshold: 0.25 })
      if (this.isHoriPageInitPreloadActive && this.containerRef.current) this.intersectionObserver.observe(this.containerRef.current)
      else this.addLoadingItems()
    }
  }

  componentWillUnmount() {
    this.clockUpdate && window.clearInterval(this.clockUpdate)
  }

  carouselInteractionHandler = () => {
    if (!this.props.canLazyLoad || this.isGettingEnriched) {
      this.setCollectionDirty()
    }
  }

  createCard = (card: StorefrontStateItem, i: number, lazyThreshold: number, inView: boolean) => {
    const item = overwriteWithCurrentSchedule(this.state.clock, card)
    const isFilteredOut = this.props.filterOn && !safeBool(item.filterEntitled)
    const isAfterThreshold = i > lazyThreshold
    return (<Card
      key={`${item.title} ${i}`}
      {...item}
      collectionType={this.props.collectionType}
      isEditActive={this.state.isEditActive}
      removeItem={(removeFromWatchNext?: boolean) => this.props.removeItem(i, this.props.collectionKey, removeFromWatchNext)}
      triggerCarouselLazyload={this.carouselInteractionHandler}
      isLazy={!this.isHoriPagActive && !this.state.isDirty && isAfterThreshold}
      hoverClass={this.props.hoverClass}
      featureSwitches={this.props.featureSwitches}
      description={item.title}
      isRemoved={isFilteredOut || item.isRemoved}
      pos={i}
      canLivePlayback={this.props.canLivePlayback}
      showProgressBar={Boolean(this.props.edit) || Boolean(item.station)}
      showPlayButton={this.props.showPlayButton || Boolean(item.station)}
      isVerticallyVisible={inView}
      isPhablet={this.props.isPhablet}
      showPrerollsInHover={safeBool(this.props.showPrerollsInHover)}
      takeFocusForCarousel={this.props.requestFocusForHover}
      resetFocusForCarousel={this.props.resetFocusForHover}
      isHoverDisabled={this.state.isEditActive}
      isOnLastCarousel={this.props.isLast}
      onFocus={() => { this.props.requestFocusForHover && this.props.requestFocusForHover() }}
    />)
  }

  createLoadingItems(numberOfCards: number) {
    const LOADING_CARD = {
      itemType: "Loading",
      entitlements: [] as string[],
      properties: {}
    } as StorefrontStateItem

    const cards = []

    for (let i = 0; i < numberOfCards; i++) {
      let clone = Object.assign({}, LOADING_CARD)
      clone.title = "Loading" + i
      cards.push(clone)
    }

    return cards
  }

  getCarouselItems(items: StorefrontStateItem[], inView: boolean) {
    const LAZY_THRESHOLD = 8
    const SQUARE_LAZY_THRESHOLD = 14
    const FACTOR = this.props.collectionType === "DoubleDeckerCarousel" ? 2 : 1
    // The portrait carousel currently has the same "weight" as the square carousel
    const thisCarouselLazyThreshold = ((this.props.collectionType === "SquareCarousel" || this.props.collectionType === "PortraitCarousel") ? SQUARE_LAZY_THRESHOLD : LAZY_THRESHOLD) * FACTOR
    const truncatedItems = this.state.truncateItemList ? items.slice(0, thisCarouselLazyThreshold + 1) : items

    if (this.props.collectionType === "DoubleDeckerCarousel") {
      const pairedTruncatedItems: StorefrontStateItem[][] = truncatedItems.reduce((result, _, index, array) => {
        if (index % 2 === 0)
          result.push(array.slice(index, index + 2));
        return result;
      }, [] as StorefrontStateItem[][])

      const pairedCarouselItems = pairedTruncatedItems.map((pair, i) => {
        return (
          <li key={pair.map(_ => _.title).join()} className={cardStyle.DoubleCardWrapper}>
            {pair.map((item) => this.createCard(item, i, thisCarouselLazyThreshold, inView))
            }
          </li>
        )
      })

      return pairedCarouselItems
    } else {
      const carouselItems = truncatedItems.filter(item => item).map((item, i) => this.createCard(item, i, thisCarouselLazyThreshold, inView))

      return carouselItems
    }
  }

  render() {
    const props = this.props
    const items = props.items

    const onCarouselScroll = this.isHoriPagActive ? undefined : this.carouselScrollInteractionHandler
    const onMoveTo = this.isHoriPagActive ? undefined : this.carouselNavClickInteractionHandler
    const isPaginatedCarousel = this.isHoriPagActive && props.paginationTargetId !== undefined

    return (
      <div
        ref={this.containerRef}
        className={cn(style.FacetedCarousel, 'tst-faceted-carousel', {
          'tst-editable-carousel': Boolean(this.props.edit),
          ['tst-paginated-carousel']: isPaginatedCarousel,
          ['mobile-collection']: props.isPhablet,
          ['min-width-collection']: props.featureSwitches &&
            props.featureSwitches.requiresMinimumPageWidth,
          [style.overflowVisible]: this.props.featureSwitches.hover2019
        })}
      >
        <Header
          {...props}
          toggleEdit={this.toggleEdit}
          isEditActive={this.state.isEditActive}
          seeMoreDescription={this.props.seeMoreDescription}
          isPaginatedCarousel={isPaginatedCarousel}
        />
        {
          <InView triggerOnce={true}>
            {(inview) =>
              <div
                onScroll={onCarouselScroll}>
                <Carousel
                  unusableVisibleWidth={parseInt(style.unusedCarouselSpace, 10)}
                  moveToListener={onMoveTo}
                  wrapChildren={false}
                  className={cn({
                    [style.focusControl]: !this.state.isEditActive && this.props.collectionHasFocusForHover,
                    [style.isLast]: this.props.isLast
                  })}
                  leftScrollButtonClassName={style.scrollButton}
                  rightScrollButtonClassName={style.scrollButton}
                >
                  {this.getCarouselItems(items, inview)}
                </Carousel>
              </div>}
          </InView>
        }
      </div>
    )
  }
}

export default FacetedCarousel
